import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Expand } from "lucide-react";
import Reveal from "@/components/Reveal";

const PHOTOS = [
  {
    src: "/assets/podio-campeao.jpeg",
    alt: "Aluno mirim do Projeto Bonsai no pódio em primeiro lugar no Alumínio Jiu-Jitsu Open",
    caption: "ESPÍRITO DE COMPETIÇÃO",
    span: "md:col-span-2 md:col-start-2 md:row-span-2",
  },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <section id="galeria" className="relative py-28 sm:py-36" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] text-gold mb-4" data-testid="gallery-eyebrow">GALERIA</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl" data-testid="gallery-title">
            Momentos que florescem <span className="text-gold">no tatame.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[220px] gap-4">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={(i % 4) * 0.1} className={p.span}>
              <button
                onClick={() => setActive(p)}
                data-testid={`gallery-photo-${i}`}
                className="group relative w-full h-full rounded-2xl overflow-hidden border border-white/10 hover:border-gold/50 transition-all duration-500 text-left"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top grayscale-[45%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-obsidian/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between gap-3">
                  <p className="font-mono text-xs tracking-[0.25em] text-gold translate-y-1 group-hover:translate-y-0 transition-transform duration-500">{p.caption}</p>
                  <span className="shrink-0 w-8 h-8 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <Expand size={14} />
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-8 font-display text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-2xl" data-testid="gallery-competition-text">
            O projeto Bonsai BJJ leva os alunos para participações em competições, trabalhando a{" "}
            <span className="text-gold">perseverança, persistência e resiliência</span> no tatame e na vida.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-obsidian/95 backdrop-blur-sm flex items-center justify-center p-5 sm:p-10"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <motion.figure
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 10 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={active.src} alt={active.alt} className="w-full max-h-[78vh] object-contain rounded-2xl border border-gold/25 shadow-[0_0_80px_rgba(212,175,55,0.15)]" />
              <figcaption className="mt-4 flex items-center justify-between gap-4">
                <p className="font-mono text-xs tracking-[0.25em] text-gold">{active.caption}</p>
                <button
                  onClick={() => setActive(null)}
                  data-testid="gallery-lightbox-close"
                  className="inline-flex items-center gap-2 font-mono text-xs tracking-widest text-zinc-400 hover:text-gold transition-colors"
                  aria-label="Fechar"
                >
                  <X size={16} /> FECHAR
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
